/**
 * The single DATA-field control. Three source groups: the workflow's Input
 * slots, upstream step outputs, and a static value.
 */

import React from 'react';
import clsx from 'clsx';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon, LinkIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { useBuilder } from '~/pages/workflows/builder/state/context';
import { PRIMITIVE_BY_KEY } from '~/pages/workflows/builder/fixtures/registry';
import {
  isAssignableToField,
  listBindingCandidates
} from '~/pages/workflows/builder/state/selectors';
import type { BindingSource, InputFieldDescriptor } from '~/pages/workflows/builder/types';

export interface BindingPickerProps {
  nodeId: string;
  field: InputFieldDescriptor;
}

type OptionKey = string;

function sourceKey(source: BindingSource): OptionKey | null {
  if (source.type === 'TASK_OUTPUT') return `out:${source.sourceNodeId}:${source.sourceOutputKey}`;
  if (source.type === 'WORKFLOW_INPUT') return `slot:${source.workflowInputKey}`;
  return 'static';
}

export function BindingPicker(props: BindingPickerProps) {
  const { state, dispatch } = useBuilder();
  const node = state.nodes.find((n) => n.id === props.nodeId);
  if (!node) return null;

  const binding = node.bindings.find((b) => b.inputKey === props.field.key);

  const candidates = listBindingCandidates(state, props.nodeId);

  type Opt = {
    key: OptionKey;
    label: string;
    description?: string;
    compatible: boolean;
    source: BindingSource | 'static';
  };
  const options: Opt[] = [
    ...candidates.slots.map<Opt>((c) => ({
      key: `slot:${c.slotKey}`,
      label: `Input · ${c.label}`,
      description: c.description,
      compatible: isAssignableToField(c.valueType, props.field),
      source: { type: 'WORKFLOW_INPUT', workflowInputKey: c.slotKey! }
    })),
    ...candidates.outputs.map<Opt>((c) => ({
      key: `out:${c.sourceNodeId}:${c.sourceOutputKey}`,
      label: `Step · ${c.label}`,
      description: c.description,
      compatible: isAssignableToField(c.valueType, props.field),
      source: {
        type: 'TASK_OUTPUT',
        sourceNodeId: c.sourceNodeId!,
        sourceOutputKey: c.sourceOutputKey!
      }
    }))
  ];

  const selectedKey = binding ? (sourceKey(binding.source) ?? 'static') : null;
  const selected = options.find((o) => o.key === selectedKey) ?? null;

  const clear = () =>
    dispatch({
      type: 'set-binding',
      nodeId: props.nodeId,
      inputKey: props.field.key,
      source: null
    });

  const setSource = (source: BindingSource) => {
    dispatch({ type: 'set-binding', nodeId: props.nodeId, inputKey: props.field.key, source });
  };

  return (
    <div>
      <Listbox
        value={selected}
        onChange={(opt: Opt | null) => {
          if (!opt) return;
          if (opt.source === 'static') {
            setSource({ type: 'STATIC', staticValue: '' });
          } else {
            setSource(opt.source);
          }
        }}
      >
        <div className="relative">
          <ListboxButton
            className={clsx(
              'flex w-full items-center gap-2 rounded-md border bg-white dark:bg-neutral-800 py-1.5 pl-2 pr-8 text-left text-sm',
              binding
                ? 'border-primary-200 dark:border-primary-500/30'
                : props.field.required
                  ? 'border-rose-300 dark:border-rose-500/30'
                  : 'border-neutral-200 dark:border-neutral-700',
              'hover:border-neutral-300 dark:hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/30'
            )}
          >
            <LinkIcon className="h-3.5 w-3.5 flex-none text-neutral-400 dark:text-neutral-500" />
            <span
              className={clsx(
                'flex-1 truncate',
                !binding && 'text-neutral-400 dark:text-neutral-500'
              )}
            >
              {binding
                ? (selected?.label ??
                  (binding.source.type === 'STATIC' ? 'Static value' : 'Select a source…'))
                : 'Select a source…'}
            </span>
            <ChevronUpDownIcon className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
          </ListboxButton>
          <ListboxOptions
            anchor="bottom start"
            className="z-50 mt-1 w-[--button-width] max-h-80 overflow-auto rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-lg focus:outline-none"
          >
            <OptGroup label="Inputs">
              {options.filter((o) => o.key.startsWith('slot:')).length === 0 && (
                <div className="px-3 py-2 text-xs italic text-neutral-400 dark:text-neutral-500">
                  No workflow inputs yet.
                </div>
              )}
              {options
                .filter((o) => o.key.startsWith('slot:'))
                .map((o) => (
                  <OptItem key={o.key} opt={o} />
                ))}
            </OptGroup>
            <OptGroup label="Upstream steps">
              {options.filter((o) => o.key.startsWith('out:')).length === 0 && (
                <div className="px-3 py-2 text-xs italic text-neutral-400 dark:text-neutral-500">
                  No upstream steps yet.
                </div>
              )}
              {options
                .filter((o) => o.key.startsWith('out:'))
                .map((o) => (
                  <OptItem key={o.key} opt={o} />
                ))}
            </OptGroup>
            <OptGroup label="Static">
              <ListboxOption
                value={
                  {
                    key: 'static',
                    label: 'Static value',
                    compatible: true,
                    source: 'static'
                  } as Opt
                }
                className="cursor-pointer px-3 py-1.5 text-sm data-[focus]:bg-primary-50 dark:data-[focus]:bg-primary-500/10"
              >
                <div>Use a static value…</div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                  Type the value inline.
                </div>
              </ListboxOption>
            </OptGroup>
          </ListboxOptions>
        </div>
      </Listbox>

      {binding && binding.source.type === 'STATIC' && (
        <div className="mt-2">
          <StaticValueEditor
            field={props.field}
            value={binding.source.staticValue}
            onChange={(v) => setSource({ type: 'STATIC', staticValue: v })}
          />
        </div>
      )}

      {binding &&
        binding.source.type === 'TASK_OUTPUT' &&
        PRIMITIVE_BY_KEY[node.taskKey].category !== 'input' && (
          <PathEditor
            source={binding.source}
            onChange={(path) =>
              setSource({
                type: 'TASK_OUTPUT',
                sourceNodeId: (binding.source as Extract<BindingSource, { type: 'TASK_OUTPUT' }>)
                  .sourceNodeId,
                sourceOutputKey: (binding.source as Extract<BindingSource, { type: 'TASK_OUTPUT' }>)
                  .sourceOutputKey,
                path
              })
            }
          />
        )}

      {binding && (
        <button
          type="button"
          onClick={clear}
          className="mt-1 inline-flex items-center gap-1 text-[11px] text-neutral-400 dark:text-neutral-500 hover:text-rose-600 dark:hover:text-rose-400"
        >
          <XMarkIcon className="h-3 w-3" />
          Clear binding
        </button>
      )}
    </div>
  );
}

function OptGroup(props: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
        {props.label}
      </div>
      <div>{props.children}</div>
    </div>
  );
}

function OptItem(props: {
  opt: {
    key: string;
    label: string;
    description?: string;
    compatible: boolean;
    source: BindingSource | 'static';
  };
}) {
  return (
    <ListboxOption
      value={props.opt}
      className={clsx(
        'cursor-pointer px-3 py-1.5 text-sm data-[focus]:bg-primary-50 dark:data-[focus]:bg-primary-500/10',
        !props.opt.compatible && 'opacity-60'
      )}
    >
      <div
        className={clsx(
          'font-medium',
          !props.opt.compatible && 'text-neutral-500 dark:text-neutral-400'
        )}
      >
        {props.opt.label}
      </div>
      {props.opt.description && (
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          {props.opt.description}
        </div>
      )}
      {!props.opt.compatible && (
        <div className="text-[10px] text-amber-600 dark:text-amber-400">
          Type mismatch — double-check before using.
        </div>
      )}
    </ListboxOption>
  );
}

function StaticValueEditor(props: {
  field: InputFieldDescriptor;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (props.field.valueType === 'number') {
    return (
      <input
        type="number"
        value={typeof props.value === 'number' ? props.value : ((props.value as string) ?? '')}
        onChange={(e) => props.onChange(Number(e.target.value))}
        className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 py-1.5 px-2 text-sm focus:border-primary-300 dark:focus:border-primary-500/30 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/30"
      />
    );
  }
  if (props.field.valueType === 'bool') {
    return (
      <select
        value={String(props.value ?? 'false')}
        onChange={(e) => props.onChange(e.target.value === 'true')}
        className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 py-1.5 px-2 text-sm focus:border-primary-300 dark:focus:border-primary-500/30 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/30"
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  return (
    <textarea
      rows={3}
      value={
        typeof props.value === 'string' ? props.value : JSON.stringify(props.value ?? '', null, 2)
      }
      onChange={(e) => props.onChange(e.target.value)}
      className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 py-1.5 px-2 font-mono text-xs focus:border-primary-300 dark:focus:border-primary-500/30 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/30"
    />
  );
}

function PathEditor(props: {
  source: Extract<BindingSource, { type: 'TASK_OUTPUT' }>;
  onChange: (path: string | undefined) => void;
}) {
  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      <span className="text-[11px] text-neutral-400 dark:text-neutral-500">Path</span>
      <input
        type="text"
        placeholder="(optional) e.g. address.line1"
        value={props.source.path ?? ''}
        onChange={(e) => props.onChange(e.target.value || undefined)}
        className="flex-1 rounded border border-neutral-200 dark:border-neutral-700 px-1.5 py-0.5 text-[11px] font-mono focus:border-primary-300 dark:focus:border-primary-500/30 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/30"
      />
    </div>
  );
}
